import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import AnimateOnScroll from './AnimateOnScroll.js';

const faqData = [
  {
    category: "General",
    questions: [
      {
        q: "What industries do you serve for vegetation management in BC?",
        a: (
          <>
            We serve <Link to="/industries/railroad" className="text-green-700 underline hover:text-green-900">railroads</Link>, pulp mills, <Link to="/industries/oil-gas" className="text-green-700 underline hover:text-green-900">oil refineries</Link>, <Link to="/industries/municipal" className="text-green-700 underline hover:text-green-900">municipalities</Link>, <Link to="/industries/utility" className="text-green-700 underline hover:text-green-900">utility companies</Link>, pipeline operators, and other industrial clients across British Columbia.
          </>
        ),
        aText: "We serve railroads, pulp mills, oil refineries, municipalities, utility companies, pipeline operators, and other industrial clients across British Columbia."
      },
      {
        q: "What areas of British Columbia do you serve?",
        a: (
          <>
            We provide vegetation management services across British Columbia, including the <Link to="/service-areas/okanagan" className="text-green-700 underline hover:text-green-900">Okanagan</Link>, <Link to="/service-areas/lower-mainland" className="text-green-700 underline hover:text-green-900">Lower Mainland</Link>, <Link to="/service-areas/interior-bc" className="text-green-700 underline hover:text-green-900">Interior BC</Link>, <Link to="/service-areas/kootenays" className="text-green-700 underline hover:text-green-900">Kootenays</Link>, and <Link to="/service-areas/northern-bc" className="text-green-700 underline hover:text-green-900">Northern BC</Link>.
          </>
        ),
        aText: "We provide vegetation management services across British Columbia, including the Okanagan, Lower Mainland, Interior BC, Kootenays, and Northern BC."
      }
    ]
  },
  {
    category: "Technical",
    questions: [
      {
        q: "What is bare ground vegetation control?",
        a: (
          <>
            Bare ground control uses pre-emergent and post-emergent herbicides to achieve total vegetation elimination in areas that must remain weed-free, such as rail yards, substations, tank farms, and gravel pads. Learn more about our <Link to="/services" className="text-green-700 underline hover:text-green-900">herbicide application services</Link>.
          </>
        ),
        aText: "Bare ground control uses pre-emergent and post-emergent herbicides to achieve total vegetation elimination in areas that must remain weed-free, such as rail yards, substations, tank farms, and gravel pads."
      },
      {
        q: "What do I receive after a treatment?",
        a: (
          <>
            You receive an invoice including the treatment date, line items explaining what was sprayed, where it was sprayed, how much was applied, and the price. We follow up after giving the products the appropriate amount of time to work to ensure everything went smoothly. See our <Link to="/processes" className="text-green-700 underline hover:text-green-900">processes</Link> for more detail.
          </>
        ),
        aText: "You receive an invoice including the treatment date, line items explaining what was sprayed, where it was sprayed, how much was applied, and the price. We follow up after giving the products the appropriate amount of time to work to ensure everything went smoothly."
      },
      {
        q: "How long do herbicides take to work?",
        a: (
          <>
            It varies depending on the product, target vegetation, and environmental conditions. Generally, visible results appear within 7 to 21 days for most post-emergent herbicides, while pre-emergent products work preventatively over the course of the season. Some treatments may take longer in cooler weather or on established perennial weeds. If you have questions about a specific treatment after application, please <Link to="/contact" className="text-green-700 underline hover:text-green-900">reach out to us</Link>.
          </>
        ),
        aText: "It varies depending on the product, target vegetation, and environmental conditions. Generally, visible results appear within 7 to 21 days for most post-emergent herbicides, while pre-emergent products work preventatively over the course of the season. Some treatments may take longer in cooler weather or on established perennial weeds. If you have questions about a specific treatment after application, please reach out to us."
      }
    ]
  },
  {
    category: "Compliance",
    questions: [
      {
        q: "Are your herbicide applicators certified in British Columbia?",
        a: (
          <>
            Yes. All technicians hold BC Industrial Vegetation and Noxious Weed Pesticide Applicator Certificates issued under the BC Integrated Pest Management Act. We are also Avetta certified, IVMA members, and WorkSafeBC compliant. Read more about our <Link to="/ecopractices" className="text-green-700 underline hover:text-green-900">environmental practices</Link>.
          </>
        ),
        aText: "Yes. All technicians hold BC Industrial Vegetation and Noxious Weed Pesticide Applicator Certificates issued under the BC Integrated Pest Management Act. We are also Avetta certified, IVMA members, and WorkSafeBC compliant."
      },
      {
        q: "What is Integrated Pest Management (IPM) and do you follow it?",
        a: (
          <>
            IPM is a science-based approach that uses the minimum effective herbicide application combined with prevention and monitoring. Boultbee has followed IPM principles since before BC legislation required it. Visit our <Link to="/ecopractices" className="text-green-700 underline hover:text-green-900">eco practices page</Link> to learn more.
          </>
        ),
        aText: "IPM is a science-based approach that uses the minimum effective herbicide application combined with prevention and monitoring. Boultbee has followed IPM principles since before BC legislation required it."
      }
    ]
  },
  {
    category: "Commercial",
    questions: [
      {
        q: "How do I get a quote for industrial weed control?",
        a: (
          <>
            <Link to="/contact" className="text-green-700 underline hover:text-green-900">Contact us</Link> at (250) 492-0296 or submit a request through our website. We will assess your site, identify target vegetation, and provide a detailed program proposal.
          </>
        ),
        aText: "Contact us at (250) 492-0296 or submit a request through our website. We will assess your site, identify target vegetation, and provide a detailed program proposal."
      },
      {
        q: "Can you provide herbicide application for railroad rights-of-way?",
        a: (
          <>
            Yes. We operate high-rail equipped herbicide application vehicles and have decades of experience with railroad vegetation management across BC. Learn more about our <Link to="/industries/railroad" className="text-green-700 underline hover:text-green-900">railroad vegetation management</Link>.
          </>
        ),
        aText: "Yes. We operate high-rail equipped herbicide application vehicles and have decades of experience with railroad vegetation management across BC."
      }
    ]
  }
];

const allQuestions = faqData.flatMap(cat => cat.questions);

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (idx) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  let globalIndex = 0;

  return (
    <div className="bg-custom-earth">
      <div className="wood-container">
        <div className="min-h-screen flex flex-col">
          <Helmet>
            <title>FAQ | Industrial Vegetation Management | Boultbee</title>
            <meta name="description" content="Answers to common questions about industrial herbicide application, IPM compliance, certifications, GPS tracking, and vegetation management services in BC." />
            <link rel="canonical" href="https://www.weedmanager.com/faq" />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Boultbee Vegetation Management" />
            <meta property="og:title" content="FAQ | Industrial Vegetation Management | Boultbee" />
            <meta property="og:description" content="Answers to common questions about industrial herbicide application, IPM compliance, certifications, GPS tracking, and vegetation management services in BC." />
            <meta property="og:url" content="https://www.weedmanager.com/faq" />
            <meta property="og:image" content="https://www.weedmanager.com/images/og-faq.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="FAQ | Industrial Vegetation Management | Boultbee" />
            <meta name="twitter:description" content="Answers to common questions about industrial herbicide application, IPM compliance, certifications, GPS tracking, and vegetation management services in BC." />
            <meta name="twitter:image" content="https://www.weedmanager.com/images/og-faq.jpg" />
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.weedmanager.com/" },
                  { "@type": "ListItem", "position": 2, "name": "FAQ", "item": "https://www.weedmanager.com/faq" }
                ]
              })}
            </script>
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": allQuestions.map(item => ({
                  "@type": "Question",
                  "name": item.q,
                  "acceptedAnswer": { "@type": "Answer", "text": item.aText }
                }))
              })}
            </script>
          </Helmet>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 text-center w-full">Frequently Asked Questions</h1>
              <p className="text-gray-700 text-center mt-3 text-lg">Common questions about our industrial vegetation management and herbicide application services in British Columbia.</p>
            </div>
          </AnimateOnScroll>

          {faqData.map((category) => (
            <AnimateOnScroll key={category.category} animation="fade-in-up">
              <div className="text-container-blur">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">{category.category}</h2>
                <div className="space-y-3">
                  {category.questions.map((item) => {
                    const idx = globalIndex++;
                    return (
                      <div key={idx} className="border border-gray-300 rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggle(idx)}
                          className="w-full text-left px-5 py-4 bg-white hover:bg-gray-50 flex justify-between items-center transition-colors"
                        >
                          <span className="font-semibold text-gray-900 pr-4">{item.q}</span>
                          <span className="text-green-700 text-xl flex-shrink-0">{openIndex === idx ? '−' : '+'}</span>
                        </button>
                        {openIndex === idx && (
                          <div className="px-5 py-4 bg-gray-50 text-gray-700 border-t border-gray-200">
                            {item.a}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </AnimateOnScroll>
          ))}

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur text-center">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Still Have Questions?</h2>
              <p className="text-gray-700 mb-5">Contact our team for a free consultation about your vegetation management needs.</p>
              <Link to="/contact" className="cta-button">
                Contact Us
              </Link>
            </div>
          </AnimateOnScroll>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
